import React, { useEffect, useState } from 'react';

import { ClipboardList, MailIcon } from 'lucide-react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'components/Accordion';
import ErrorBoundary from 'components/ErrorBoundary';
import { TooltipProvider } from 'components/Tooltip';
import SidebarLoader from 'components/loading/SidebarLoader';
import { Typography } from 'components/typography';
import useLogPostHogPageView from 'hooks/useLogPostHogPageView';
import { useServiceFeatures } from 'hooks/useServiceContext';
import RequestCarrierQuotesByGroup from 'pages/QuoteView/Quoting/CarrierQuote/RequestCarrierQuotesByGroup';
import RequestCarrierQuotesByLocation from 'pages/QuoteView/Quoting/CarrierQuote/RequestCarrierQuotesByLocation';
import ReviewCarrierQuotesSection from 'pages/QuoteView/Quoting/CarrierQuote/ReviewCarrierQuotes';
import { Email } from 'types/Email';
import { QuoteRequest } from 'types/QuoteRequest';
import { Maybe, Undef } from 'types/UtilityTypes';
import Pageview from 'types/enums/Pageview';

enum CarrierQuoteSections {
  RequestQuotes = 'request-quotes',
  ReviewQuotes = 'review-quotes',
}

interface CarrierQuoteTabProps {
  email: Maybe<Email>;
  quoteRequest: Maybe<QuoteRequest>;
  setQuoteRequest: React.Dispatch<React.SetStateAction<Maybe<QuoteRequest>>>;
  isLoadingRequest: boolean;
}

export default function CarrierQuoteTab({
  email,
  quoteRequest,
  setQuoteRequest,
  isLoadingRequest,
}: CarrierQuoteTabProps) {
  const { serviceID, carrierQuoteConfig } = useServiceFeatures();

  useLogPostHogPageView(Pageview.CarrierQuote, {
    service_id: serviceID,
    email_id: email?.id || null,
  });

  const [accordionTab, setAccordionTab] =
    useState<Undef<CarrierQuoteSections[]>>(undefined);

  useEffect(() => {
    if (isLoadingRequest) {
      return;
    }
    if (!quoteRequest) {
      setAccordionTab([CarrierQuoteSections.RequestQuotes]);
      return;
    }

    if (
      !quoteRequest.carrierNetworkEmails ||
      quoteRequest.carrierNetworkEmails.length === 0
    ) {
      setAccordionTab([CarrierQuoteSections.RequestQuotes]);
      return;
    }

    setAccordionTab([CarrierQuoteSections.ReviewQuotes]);
  }, [quoteRequest, isLoadingRequest]);

  if (!carrierQuoteConfig) {
    return (
      <Typography align='center' variant='body-sm' className='p-4'>
        No Carrier Quote configuration found. Please contact the Drumkit team
        for help.
      </Typography>
    );
  }

  return (
    <div className=''>
      <TooltipProvider>
        {isLoadingRequest && <SidebarLoader />}
        <Accordion
          type='multiple'
          value={accordionTab}
          onValueChange={(v) => setAccordionTab(v as CarrierQuoteSections[])}
        >
          {!isLoadingRequest && (
            <div>
              {/* Email Carriers Section */}
              <AccordionItem value={CarrierQuoteSections.RequestQuotes}>
                <AccordionTrigger
                  icon={<MailIcon className='h-6 w-6' strokeWidth={1} />}
                >
                  Email Carriers
                </AccordionTrigger>
                <AccordionContent>
                  <ErrorBoundary>
                    {carrierQuoteConfig.isFindCarrierByLocationEnabled && (
                      <RequestCarrierQuotesByLocation
                        email={email}
                        quoteRequest={quoteRequest}
                        setQuoteRequest={setQuoteRequest}
                        carrierQuoteConfig={carrierQuoteConfig}
                      />
                    )}
                    {carrierQuoteConfig.isFindCarrierByGroupEnabled && (
                      <RequestCarrierQuotesByGroup
                        email={email}
                        quoteRequest={quoteRequest}
                        setQuoteRequest={setQuoteRequest}
                        carrierQuoteConfig={carrierQuoteConfig}
                      />
                    )}
                  </ErrorBoundary>
                </AccordionContent>
              </AccordionItem>

              {/* Review Carrier Responses Section */}
              {!isLoadingRequest &&
                quoteRequest &&
                quoteRequest.carrierNetworkEmails &&
                quoteRequest.carrierNetworkEmails.length > 0 && (
                  <AccordionItem value={CarrierQuoteSections.ReviewQuotes}>
                    <AccordionTrigger
                      icon={
                        <ClipboardList className='h-6 w-6' strokeWidth={1} />
                      }
                    >
                      Review Responses
                    </AccordionTrigger>
                    <AccordionContent>
                      <ErrorBoundary>
                        <ReviewCarrierQuotesSection
                          requestId={quoteRequest.id}
                          emails={quoteRequest.carrierNetworkEmails}
                          carrierQuotes={quoteRequest.carrierQuotes ?? []}
                        />
                      </ErrorBoundary>
                    </AccordionContent>
                  </AccordionItem>
                )}
            </div>
          )}
        </Accordion>
      </TooltipProvider>
    </div>
  );
}
